import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Input, Checkbox, Button } from 'antd';
import { FloppyDisk, Trash } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useOneButtonCapability } from '../../../../hooks/useOneButtonCapability';
import {
    buildContactFormStatesForValidation,
    duplicateContactEmailIndexes,
    getContactValidationErrors,
    PRIMARY_CONTACT_LIMIT_MESSAGE,
    type ContactValidationAlert,
} from '../../../../helpers/contactFormValidation';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/ContactManageModal.css';
import './oneButton-css/OneButtonFormControls.css';

/** Portal target in DashboardModal footer (split contacts layout). */
export const OBD_CONTACTS_PANEL_FOOTER_ACTIONS_ID = 'obd-contacts-panel-footer-actions';

export interface ContactFormState {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phoneType: string;
    contactType: string;
    preferredContactMethod: string;
    preferredLanguage: string;
    isPrimaryContact: boolean;
    isSecondaryContact: boolean;
    onUtilityBill: boolean;
    addToContract: boolean;
    homeowner: boolean;
    onTitle: boolean;
    sameAsInstallationAddress: boolean;
    mailingStreet: string;
    mailingCity: string;
    mailingState: string;
    mailingZip: string;
    companyTag?: string;
    contactLabel?: string;
}

export interface ValidationErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    mailingStreet?: string;
    mailingCity?: string;
    mailingState?: string;
    mailingZip?: string;
    isPrimaryContact?: string;
}


const CONTACT_TYPE_OPTIONS = [
    { id: 'primary', label: 'Primary Contact', key: 'isPrimaryContact' as const },
    { id: 'secondary', label: 'Secondary Contact', key: 'isSecondaryContact' as const },
    { id: 'utility', label: 'On Utility Bill', key: 'onUtilityBill' as const },
    { id: 'contract', label: 'Add to Contract', key: 'addToContract' as const },
    { id: 'homeowner', label: 'Homeowner', key: 'homeowner' as const },
    { id: 'onTitle', label: 'On Title', key: 'onTitle' as const },
];

interface SelectOption {
    value: string;
    label: string;
}

const defaultFormState: ContactFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneType: '2',
    contactType: '77',
    preferredContactMethod: '6',
    preferredLanguage: '4',
    isPrimaryContact: false,
    isSecondaryContact: false,
    onUtilityBill: false,
    addToContract: false,
    homeowner: false,
    onTitle: false,
    sameAsInstallationAddress: true,
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    companyTag: '',
    contactLabel: '',
};

function customerToFormState(c: any): ContactFormState {
    return {
        firstName: c?.FirstName ?? c?.firstName ?? '',
        lastName: c?.LastName ?? c?.lastName ?? '',
        email: c?.Email ?? c?.email ?? '',
        phone: c?.Phone ?? c?.phone ?? '',
        phoneType: (c?.PhoneType !== undefined && c?.PhoneType !== null) ? String(c.PhoneType) : (c?.phoneType !== undefined && c?.phoneType !== null ? String(c.phoneType) : ''),
        contactType: (c?.ContactType !== undefined && c?.ContactType !== null) ? String(c.ContactType) : (c?.contactType !== undefined && c?.contactType !== null ? String(c.contactType) : ''),
        preferredContactMethod: (c?.PreferredContactMethod !== undefined && c?.PreferredContactMethod !== null) ? String(c.PreferredContactMethod) : (c?.preferredContactMethod !== undefined && c?.preferredContactMethod !== null ? String(c.preferredContactMethod) : ''),
        preferredLanguage: (c?.PreferredLanguage !== undefined && c?.PreferredLanguage !== null) ? String(c.PreferredLanguage) : (c?.preferredLanguage !== undefined && c?.preferredLanguage !== null ? String(c.preferredLanguage) : ''),
        isPrimaryContact: !!(c?.IsPrimaryContact ?? c?.isPrimaryContact),
        isSecondaryContact: !!(c?.IsSecondaryContact ?? c?.isSecondaryContact),
        onUtilityBill: !!(c?.IsUtilityBillOwner ?? c?.isUtilityBillOwner),
        addToContract: !!(c?.AddToContract ?? c?.addToContract),
        homeowner: !!(c?.OnTitleCheck ?? c?.onTitleCheck)
            ? false
            : !!(c?.IsHomeowner ?? c?.IsHomeOwner ?? c?.isHomeowner ?? c?.isHomeOwner),
        onTitle: !!(c?.OnTitleCheck ?? c?.onTitleCheck),
        sameAsInstallationAddress: c?.SameAsInstallationAddress ?? c?.sameAsInstallationAddress ?? true,
        mailingStreet: c?.InsStreet ?? c?.insStreet ?? c?.MailingStreet ?? c?.mailingStreet ?? c?.OtherStreet ?? c?.otherStreet ?? c?.Street ?? c?.street ?? '',
        mailingCity: c?.InsCity ?? c?.insCity ?? c?.MailingCity ?? c?.mailingCity ?? c?.OtherCity ?? c?.otherCity ?? c?.City ?? c?.city ?? '',
        mailingState: (c?.InsState ?? c?.insState ?? c?.MailingState ?? c?.mailingState ?? c?.OtherState ?? c?.otherState ?? c?.State ?? c?.state ?? '')?.toUpperCase(),
        mailingZip: c?.InsZipCode ?? c?.insZipCode ?? c?.MailingPostalCode ?? c?.mailingPostalCode ?? c?.MailingZipCode ?? c?.mailingZipCode ?? c?.OtherZipCode ?? c?.otherZipCode ?? c?.ZipCode ?? c?.zipCode ?? '',
        companyTag: c?.Company ?? c?.CompanyName ?? c?.company ?? c?.companyName ?? '',
        contactLabel: c?.ContactLabel ?? c?.contactLabel ?? '',
    };
}

/** 
 * Formats raw digits into (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export interface ContactManageModalProps {
    open: boolean;
    onClose: () => void;
    contact: any | null;
    contactIndex: number;
    /** When provided, form initializes from this (e.g. saved overrides) merged with contact */
    initialOverride?: Partial<ContactFormState>;
    onSave?: (index: number, data: ContactFormState) => void;
    onDelete?: (index: number) => void;
    onDeleteNew?: (index: number) => void;
    onNotice?: (type: 'success' | 'error' | 'warning' | 'info', message?: string, description?: string) => void;
    /** Panel mode: live form updates (e.g. sync detail hero tags while editing). */
    onFormChange?: (data: ContactFormState) => void;
    /** Unsaved per-contact overrides (split-panel) so primary counts stay accurate before save. */
    contactOverrides?: Record<number, Partial<ContactFormState>>;
    /** Hide delete while any contact in the split modal has unsaved edits. */
    hasUnsavedChanges?: boolean;
    /** Parent list validation state (all contacts) after save attempt. */
    listValidationSubmitted?: boolean;
    listValidationErrors?: ValidationErrors;
    /** Validation summary shown inside the contacts modal (not as a toast). */
    validationAlert?: ContactValidationAlert | null;
    onValidationAlertDismiss?: () => void;
    /** `panel` renders form inline (contacts modal split); default opens nested dashboard modal */
    presentation?: 'modal' | 'panel';
}

export const ContactManageModal: React.FC<ContactManageModalProps> = ({
    open,
    onClose,
    contact,
    contactIndex,
    initialOverride,
    onSave,
    onDelete,
    onDeleteNew,
    onNotice,
    onFormChange,
    contactOverrides,
    hasUnsavedChanges = false,
    listValidationSubmitted = false,
    listValidationErrors,
    validationAlert = null,
    onValidationAlertDismiss,
    presentation = 'modal',
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityinfoMasterData = useOneButtonStore((s) => s.opportunityinfoMasterData);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const financingGrid = useOneButtonStore((s) => s.financingGrid);
    const { getCapabilityValue, hasCapability } = useOneButtonCapability();

    const [form, setForm] = useState<ContactFormState>(defaultFormState);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [mailingAddressBackup, setMailingAddressBackup] = useState<Partial<ContactFormState> | null>(null);
    
    const [warningDialog, setWarningDialog] = useState<{ title: string; content: string } | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [panelFooterPortal, setPanelFooterPortal] = useState<HTMLElement | null>(null);
    const skipFormChangeRef = useRef(true);
    const onFormChangeRef = useRef(onFormChange);
    const lastFormChangePayloadRef = useRef('');
    onFormChangeRef.current = onFormChange;

    useEffect(() => {
        if (presentation !== 'panel' || !open) {
            setPanelFooterPortal(null);
            return;
        }
        const syncPortal = () => {
            setPanelFooterPortal(document.getElementById(OBD_CONTACTS_PANEL_FOOTER_ACTIONS_ID));
        };
        syncPortal();
        const t = window.setTimeout(syncPortal, 0);
        return () => window.clearTimeout(t);
    }, [presentation, open]);

    const isLightreach = useMemo(() => {
        if (financingGrid?.some(r => (r.partner || '').toLowerCase().includes('lightreach'))) return true;
        const products = opportunityDetails?.OpportunityProduct || opportunityDetails?.opportunityProduct || [];
        return products.some((p: any) => {
            const pn = (p.Finance_Partner_Display_Name || p.Finance_Partner || '').toLowerCase();
            return pn.includes('lightreach');
        });
    }, [financingGrid, opportunityDetails]);

    const creditPassed = useMemo(() => {
        if (!contact) return false;
        if (contact.CreditCheckStatus === true || contact.creditCheckStatus === true) return true;
        const passStr = String(contact.CreditPassStatus || contact.creditPassStatus || '').toLowerCase();
        if (passStr === 'passed') return true;
        if (contact.financePartnerCreditStatusList?.IsAnyCreditStatusPass) return true;
        return false;
    }, [contact]);

    const anyCreditPassed = useMemo(() => {
        if (opportunityDetails?.IsAnyCreditPassed) return true;
        const customers = opportunityDetails?.Customers || [];
        return customers.some((c: any) => {
            // Check boolean status from provided JSON
            if (c.CreditCheckStatus === true || c.creditCheckStatus === true) return true;
            // Check string status
            const status = String(c.CreditPassStatus || c.creditPassStatus || '').toLowerCase();
            if (status === 'passed') return true;
            // Check nested financial pass status from JSON
            if (c.financePartnerCreditStatusList?.IsAnyCreditStatusPass) return true;
            return false;
        });
    }, [opportunityDetails]);

    const isFieldLocked = useMemo(() => {
        // Only lock if THIS specific contact passed credit AND it's Lightreach
        // But always allow editing for NEW contacts (no CustomerId)
        const contactId = contact?.CustomerId || contact?.customerId;
        if (!contactId) return false;
        
        return creditPassed && isLightreach;
    }, [creditPassed, isLightreach, contact]);

    const opportunityInfoAccess = useMemo(() => getCapabilityValue('Opportunity_Information_Access'), [getCapabilityValue]);
    const homeOwnerAccess = useMemo(() => getCapabilityValue('Home_Owner'), [getCapabilityValue]);

    const isOpportunityInfoReadOnly = opportunityInfoAccess === 'View';
    const showSecondaryContact = hasCapability('Secondary_Contact');
    const showHomeOwner = hasCapability('Home_Owner');
    const isHomeOwnerReadOnly = homeOwnerAccess === 'View';

    const isCapabilityReadOnly = isOpportunityInfoReadOnly;
    const isCustomerInfoAndAddressLocked = isFieldLocked || isCapabilityReadOnly;
    const isContactTypeLocked = isCapabilityReadOnly;

    const mapToOptions = (items: any[]) => 
        items?.map((item: any) => ({ 
            value: item.Value !== undefined ? String(item.Value) : (item.Id !== undefined ? String(item.Id) : String(item.Text || item)), 
            label: item.Text || String(item) 
        })) || [];

    const phoneTypes = useMemo(() => {
        const data = opportunityinfoMasterData?.PhoneType || opportunityinfoMasterData?.phoneType;
        return mapToOptions(data);
    }, [opportunityinfoMasterData]);
    const titleCase = (str: string) => 
        str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

    const getTotalAddToContractCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i === contactIndex) return;
            const override = contactOverrides?.[i];
            const isPrimary =
                override?.isPrimaryContact !== undefined
                    ? override.isPrimaryContact
                    : !!(c.IsPrimaryContact || c.isPrimaryContact);
            const isSecondary =
                override?.isSecondaryContact !== undefined
                    ? override.isSecondaryContact
                    : !!(c.IsSecondaryContact || c.isSecondaryContact);
            const isAddToContract =
                (override?.addToContract !== undefined
                    ? override.addToContract
                    : !!(c.AddToContract || c.addToContract)) ||
                isPrimary ||
                isSecondary;
            if (isAddToContract) count++;
        });
        return count;
    }, [opportunityDetails, contactIndex, contactOverrides]);

    const getTotalHomeownerCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i === contactIndex) return;
            const override = contactOverrides?.[i];
            const isHomeowner =
                override?.homeowner !== undefined
                    ? override.homeowner
                    : !!(c.IsHomeowner || c.IsHomeOwner || c.homeowner || c.isHomeowner);
            if (isHomeowner) count++;
        });
        return count;
    }, [opportunityDetails, contactIndex, contactOverrides]);

    const isCustomerPrimary = useCallback((c: any, index: number) => {
        const override = contactOverrides?.[index];
        if (override?.isPrimaryContact !== undefined) return override.isPrimaryContact;
        return !!(c?.IsPrimaryContact || c?.isPrimaryContact);
    }, [contactOverrides]);

    const getTotalPrimaryCount = useCallback(() => {
        let count = 0;
        const customers = opportunityDetails?.Customers || [];
        customers.forEach((c: any, i: number) => {
            if (i !== contactIndex && isCustomerPrimary(c, i)) count++;
        });
        return count;
    }, [opportunityDetails, contactIndex, isCustomerPrimary]);

    const otherPrimaryExists = getTotalPrimaryCount() > 0;

    const states = useMemo(() => {
        const items = opportunityinfoMasterData?.StatesFullName || [];
        return items.map((item: any) => {
            const rawLabel = item.Text || String(item);
            const rawValue = item.Value !== undefined ? String(item.Value) : (item.Id !== undefined ? String(item.Id) : String(rawLabel));
            const formattedLabel = rawLabel.split(' ').map(titleCase).join(' ');
            return { value: rawValue, label: formattedLabel };
        });
    }, [opportunityinfoMasterData]);
    const languages = useMemo(() => mapToOptions(opportunityinfoMasterData?.PreferredLanguage), [opportunityinfoMasterData]);
    const contactTypes = useMemo(() => mapToOptions(opportunityinfoMasterData?.contactType), [opportunityinfoMasterData]);
    const contactMethods = useMemo(() => mapToOptions(opportunityinfoMasterData?.PreferredContactMethod), [opportunityinfoMasterData]);

    const contactSeedKey = useMemo(() => {
        if (!contact) return `new-${contactIndex}`;
        return [
            contactIndex,
            contact.CustomerId ?? contact.customerId ?? contact.Id ?? contact.id ?? '',
            contact.OnTitleCheck ?? contact.onTitleCheck ?? '',
            contact.TitleVerified ?? contact.titleVerified ?? '',
            contact.BKTitleCheckMade ?? contact.bkTitleCheckMade ?? '',
            contact.FirstName ?? contact.firstName ?? '',
            contact.LastName ?? contact.lastName ?? '',
            contact.Email ?? contact.email ?? '',
            contact.Phone ?? contact.phone ?? '',
        ].join('|');
    }, [contact, contactIndex]);

    useEffect(() => {
        if (open) {
            setErrors({});
            setSubmitted(false);
            setMailingAddressBackup(null);
            skipFormChangeRef.current = true;

            if (contact) {
                const base = customerToFormState(contact);
                const merged = initialOverride ? { ...base, ...initialOverride } : base;
                
                // If same as installation is true, sync from opportunity details if fields are empty
                if (merged.sameAsInstallationAddress && opportunityDetails?.InstallationAddress) {
                    const addr = opportunityDetails.InstallationAddress;
                    if (!merged.mailingStreet) merged.mailingStreet = addr.Street || addr.street || '';
                    if (!merged.mailingCity) merged.mailingCity = addr.City || addr.city || '';
                    if (!merged.mailingState) merged.mailingState = addr.State || addr.state || addr.stateCode || '';
                    if (!merged.mailingZip) merged.mailingZip = addr.ZipCode || addr.zipCode || '';
                }

                // Angular setCustomerInformations: primary implies on contract when slot allows.
                if (merged.isPrimaryContact && !merged.addToContract) merged.addToContract = true;

                merged.phone = formatPhoneNumber(merged.phone);
                setForm(merged);
            } else {
                const primary = opportunityDetails?.Customers?.find((c: any) => c.IsPrimaryContact);
                const installAddr = opportunityDetails?.InstallationAddress;
                const base = { ...defaultFormState, ...initialOverride };
                
                if (primary) {
                    if (!base.email) base.email = primary.Email || '';
                    if (!base.phone) base.phone = primary.Phone || '';
                }

                if (installAddr && base.sameAsInstallationAddress) {
                    base.mailingStreet = installAddr.Street || '';
                    base.mailingCity = installAddr.City || '';
                    base.mailingState = installAddr.State || installAddr.stateCode || '';
                    base.mailingZip = installAddr.ZipCode || installAddr.zipCode || '';
                }

                setForm(base);
            }
        }
    }, [open, contactIndex, contactSeedKey]); // Re-seed when contact record changes (e.g. title sync)

    useEffect(() => {
        if (!open || presentation !== 'panel' || !onFormChangeRef.current) return;
        if (skipFormChangeRef.current) {
            skipFormChangeRef.current = false;
            lastFormChangePayloadRef.current = JSON.stringify(form);
            return;
        }
        const payload = JSON.stringify(form);
        if (payload === lastFormChangePayloadRef.current) return;
        lastFormChangePayloadRef.current = payload;
        onFormChangeRef.current(form);
    }, [form, open, presentation]);

    const displayName = `${form.firstName} ${form.lastName}`.trim() || 'New contact';

    const showValidation = submitted || listValidationSubmitted;
    const activeErrors = useMemo(
        () => ({
            ...errors,
            ...(listValidationSubmitted && listValidationErrors ? listValidationErrors : {}),
        }),
        [errors, listValidationSubmitted, listValidationErrors],
    );

    const fieldError = (field: keyof ValidationErrors) => (showValidation ? activeErrors[field] : undefined);

    const update = (key: keyof ContactFormState, value: any) => {
        setFormWithValidation((prev) => ({ ...prev, [key]: value }));
    };

    const getValidationErrors = (state: ContactFormState = form): ValidationErrors => {
        const customers = opportunityDetails?.Customers || [];
        const otherPrimaryCount = (() => {
            let count = 0;
            customers.forEach((c: any, i: number) => {
                if (i !== contactIndex && isCustomerPrimary(c, i)) count++;
            });
            return count;
        })();

        const formStates = buildContactFormStatesForValidation(
            customers,
            contactOverrides ?? {},
            contactIndex,
            state,
        );
        const duplicateEmail = duplicateContactEmailIndexes(formStates).has(contactIndex);

        return getContactValidationErrors(state, { otherPrimaryCount, duplicateEmail });
    };

    /** Pure field validation — updates inline errors but never mutates opportunity store. */
    const validate = useCallback(
        (state: ContactFormState = form): boolean => {
            const validationErrors = getValidationErrors(state);
            setErrors(validationErrors);
            return Object.keys(validationErrors).length === 0;
        },
        [form, getValidationErrors],
    );

    const setFormWithValidation = useCallback(
        (updater: (prev: ContactFormState) => ContactFormState) => {
            setForm((prev) => {
                const next = updater(prev);
                if (submitted || listValidationSubmitted) {
                    setErrors(getValidationErrors(next));
                }
                return next;
            });
        },
        [submitted, listValidationSubmitted, getValidationErrors],
    );

    useEffect(() => {
        if (!showValidation || !open) return;
        setErrors(getValidationErrors(form));
    }, [form, showValidation, open, contactIndex, contactOverrides, opportunityDetails]);

    const handleSave = () => {
        if (isCapabilityReadOnly) return;
        setSubmitted(true);

        const isBasicValid = validate(form);
        
        const totalPrimary = getTotalPrimaryCount() + (form.isPrimaryContact ? 1 : 0);
        let primaryWarning = '';
        if (totalPrimary === 0) {
            primaryWarning = 'No contact has been added as a Primary Contact';
        } else if (totalPrimary > 1) {
            primaryWarning = PRIMARY_CONTACT_LIMIT_MESSAGE;
        }

        // 3. Decision Logic — split panel validates all contacts in parent on save
        if (presentation === 'panel') {
            if (primaryWarning) {
                setWarningDialog({
                    title: totalPrimary > 1 ? 'Primary Contact Limit' : 'Primary Contact Required',
                    content: primaryWarning,
                });
                return;
            }
            onSave?.(contactIndex, form);
            return;
        }

        if (isBasicValid && !primaryWarning) {
            onSave?.(contactIndex, form);
            onClose();
            return;
        }

        // If basic fields are valid but business logic fails, show the modal warning
        if (isBasicValid) {
            if (primaryWarning) {
                setWarningDialog({
                    title: totalPrimary > 1 ? 'Primary Contact Limit' : 'Primary Contact Required',
                    content: primaryWarning,
                });
            }
        }
        // If basic fields are invalid, inline errors are shown under each field.
    };

    const canDelete = useMemo(() => {
        if (!contact) return false;
        if (isCapabilityReadOnly) return false;
        
        // Use property names from both API response and state
        const contactId = contact.CustomerId || contact.customerId;
        const isPrimary = !!(contact.IsPrimaryContact || contact.isPrimaryContact);
        const hasPassedCredit = !!(contact.CreditCheckStatus || contact.creditCheckStatus);

        // Rule: Newly added records (no CustomerId) can always be removed
        if (!contactId) return true;

        // Rule: Cannot delete if Primary, passed credit, or key legal roles are assigned
        if (isPrimary) return false;
        if (hasPassedCredit) return false;
        if (form.onTitle) return false;
        if (form.homeowner) return false;
        
        return true;
    }, [contact, form.onTitle, form.homeowner, isCapabilityReadOnly]);

    const showDeleteButton = canDelete && !hasUnsavedChanges;

    const handleDelete = () => {
        setDeleteConfirmOpen(true);
    };

    const resetIndex = () =>{
        onDeleteNew?.(contactIndex);
    }

    const actionButtons = (
        <>
            {showDeleteButton && (
                <button
                    type="button"
                    className="obd-contact-modal__btn obd-contact-modal__btn--delete"
                    onClick={handleDelete}
                >
                    <Trash size={16} weight="bold" aria-hidden />
                    Delete
                </button>
            )}
            {!contact && !!contactIndex && !hasUnsavedChanges && (
                <button
                    type="button"
                    className="obd-contact-modal__btn obd-contact-modal__btn--delete"
                    onClick={resetIndex}
                >
                    <Trash size={16} weight="bold" aria-hidden />
                    Delete
                </button>
            )}
            <button
                type="button"
                className="obd-contact-modal__btn obd-contact-modal__btn--primary"
                onClick={handleSave}
                disabled={isCapabilityReadOnly}
            >
                <FloppyDisk size={14} weight="bold" aria-hidden />
                Save
            </button>
        </>
    );

    const footerActions = <div className="obd-contact-modal__actions">{actionButtons}</div>;

    const confirmDialogs = (
        <>
            {(warningDialog || validationAlert) && (
                <DashboardModal
                    open
                    onClose={() => {
                        if (validationAlert) {
                            onValidationAlertDismiss?.();
                        } else {
                            setWarningDialog(null);
                        }
                    }}
                    title={(validationAlert ?? warningDialog)!.title}
                    width={460}
                    theme={theme}
                    className={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    rootClassName={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    zIndex={20000}
                    maskClosable={false}
                    footer={
                        <Button
                            type="primary"
                            onClick={() => {
                                if (validationAlert) {
                                    onValidationAlertDismiss?.();
                                } else {
                                    setWarningDialog(null);
                                }
                            }}
                        >
                            OK
                        </Button>
                    }
                >
                    {validationAlert?.fieldGroups?.length ? (
                        <div className="obd-contact-validation-alert">
                            {validationAlert.fieldGroups.length === 1 ? (
                                <ul className="obd-contact-validation-alert__fields">
                                    {validationAlert.fieldGroups[0].fields.map((field) => (
                                        <li key={field}>{field}</li>
                                    ))}
                                </ul>
                            ) : (
                                validationAlert.fieldGroups.map((group) => (
                                    <div key={group.contactName} className="obd-contact-validation-alert__group">
                                        <div className="obd-contact-validation-alert__name">{group.contactName}</div>
                                        <ul className="obd-contact-validation-alert__fields">
                                            {group.fields.map((field) => (
                                                <li key={`${group.contactName}-${field}`}>{field}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        <div>{(validationAlert ?? warningDialog)!.content}</div>
                    )}
                </DashboardModal>
            )}
            {deleteConfirmOpen && (
                <DashboardModal
                    open
                    onClose={() => setDeleteConfirmOpen(false)}
                    title="Delete Contact"
                    width={460}
                    theme={theme}
                    className={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    rootClassName={`obd-warning-popup ${theme === 'light' ? 'obd-theme-light' : ''}`}
                    zIndex={20000}
                    maskClosable={false}
                    footer={
                        <>
                            <Button onClick={() => setDeleteConfirmOpen(false)}>No</Button>
                            <Button
                                type="primary"
                                danger
                                onClick={() => {
                                    onDelete?.(contactIndex);
                                    setDeleteConfirmOpen(false);
                                    if (presentation === 'modal') onClose();
                                }}
                            >
                                Yes
                            </Button>
                        </>
                    }
                >
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Trash size={20} color="#ff4d4f" />
                        <span>Are you sure you want to delete {displayName}?</span>
                    </div>
                </DashboardModal>
            )}
        </>
    );

    const formFields = (
                <div className="obd-contact-modal__grid">
                    <section className="obd-contact-modal__section">
                        <h3 className="obd-contact-modal__section-title">Customer information</h3>
                        <div className="obd-contact-modal__fields">
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">First Name *</label>
                                <Input
                                    value={form.firstName}
                                    onChange={(e) => update('firstName', e.target.value)}
                                    placeholder="First name"
                                    className={`obd-contact-modal__input ${fieldError('firstName') ? 'is-invalid' : ''}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                                {fieldError('firstName') ? <div className="invalid-feedbacks">{fieldError('firstName')}</div> : null}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Last Name *</label>
                                <Input
                                    value={form.lastName}
                                    onChange={(e) => update('lastName', e.target.value)}
                                    placeholder="Last name"
                                    className={`obd-contact-modal__input ${fieldError('lastName') ? 'is-invalid' : ''}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                                {fieldError('lastName') ? <div className="invalid-feedbacks">{fieldError('lastName')}</div> : null}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Email *</label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => update('email', e.target.value)}
                                    placeholder="Email"
                                    className={`obd-contact-modal__input ${fieldError('email') ? 'is-invalid' : ''}`}
                                    disabled={isCapabilityReadOnly}
                                />
                                {fieldError('email') ? <div className="invalid-feedbacks">{fieldError('email')}</div> : null}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label className="obd-contact-modal__label-required">Phone Number *</label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) => update('phone', formatPhoneNumber(e.target.value))}
                                    placeholder="(555) 000-0000"
                                    className={`obd-contact-modal__input ${fieldError('phone') ? 'is-invalid' : ''}`}
                                    disabled={isCapabilityReadOnly}
                                    maxLength={14}
                                />
                                {fieldError('phone') ? <div className="invalid-feedbacks">{fieldError('phone')}</div> : null}
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Phone Type</label>
                                <OBDSelect
                                    value={form.phoneType}
                                    onChange={(v) => update('phoneType', v)}
                                    options={phoneTypes}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={true}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Contact Type</label>
                                <OBDSelect
                                    value={form.contactType}
                                    onChange={(v) => update('contactType', v)}
                                    options={contactTypes}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Preferred Contact Method</label>
                                <OBDSelect
                                    value={form.preferredContactMethod}
                                    onChange={(v) => update('preferredContactMethod', v)}
                                    options={contactMethods}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                            <div className="obd-contact-modal__field">
                                <label>Preferred Language</label>
                                <OBDSelect
                                    value={form.preferredLanguage}
                                    onChange={(v) => update('preferredLanguage', v)}
                                    options={languages}
                                    className="obd-contact-modal__select"
                                    popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                    disabled={isCustomerInfoAndAddressLocked}
                                />
                            </div>
                        </div>

                    </section>

                    <div className="obd-contact-modal__column">
                        <section className="obd-contact-modal__section obd-contact-modal__section--types">
                            <h3 className="obd-contact-modal__section-title">Contact type</h3>
                            <div className="obd-contact-modal__type-grid">
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.isPrimaryContact ? 'obd-contact-modal__type-tile--selected' : ''} ${form.isSecondaryContact || (otherPrimaryExists && !form.isPrimaryContact) ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                        onClick={() => {
                                            if (isContactTypeLocked) return;
                                            if (form.isSecondaryContact) return;
                                        const newVal = !form.isPrimaryContact;
                                        if (newVal) {
                                            if (getTotalPrimaryCount() > 0) {
                                                setWarningDialog({
                                                    title: 'Primary Contact Limit',
                                                    content: PRIMARY_CONTACT_LIMIT_MESSAGE,
                                                });
                                                return;
                                            }
                                            const contractCount = getTotalAddToContractCount();
                                            if (contractCount >= 2) {
                                                setWarningDialog({
                                                    title: 'Contract Limit Reached',
                                                    content: 'You can not select more than two Add to Contract. Please unselect anyone and try again.',
                                                });
                                                return;
                                            }
                                        }

                                        setFormWithValidation((prev) => ({
                                            ...prev,
                                            isPrimaryContact: newVal,
                                            isSecondaryContact: false,
                                            addToContract: newVal ? true : false,
                                        }));
                                    }}
                                >
                                    Primary contact
                                    {fieldError('isPrimaryContact') ? (
                                        <div className="invalid-feedbacks">{fieldError('isPrimaryContact')}</div>
                                    ) : null}
                                </div>
                                {showSecondaryContact && (
                                    <div 
                                        className={`obd-contact-modal__type-tile obd-contact-modal__type-tile--secondary ${form.isSecondaryContact ? 'obd-contact-modal__type-tile--selected' : ''} obd-contact-modal__type-tile--disabled`}
                                    >
                                        Secondary contact
                                    </div>
                                )}
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.onUtilityBill ? 'obd-contact-modal__type-tile--selected' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        update('onUtilityBill', !form.onUtilityBill);
                                    }}
                                >
                                    On utility bill
                                </div>
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.addToContract ? 'obd-contact-modal__type-tile--selected' : ''} ${form.isPrimaryContact || isContactTypeLocked ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        if (form.isPrimaryContact || isContactTypeLocked) return;

                                        const newVal = !form.addToContract;
                                        if (newVal) {
                                            const contractCount = getTotalAddToContractCount();
                                            if (contractCount >= 2) {
                                                setWarningDialog({
                                                    title: 'Contract Limit Reached',
                                                    content: 'You can only add up to two contacts to the contract. Please unselect anyone and try again.',
                                                });
                                                return;
                                            }

                                            const name = `${form.firstName} ${form.lastName}`.trim() || 'Customer';
                                            onNotice?.(
                                                'info',
                                                undefined,
                                                `${name} is being added as a signee to the contract by clicking on the 'Add to Contract' button and will be required to sign either Third Party or Trinity Agreements. If the 'Add to Contract' button was clicked by mistake, please uncheck it.`,
                                            );
                                        }

                                        setFormWithValidation((prev) => ({
                                            ...prev,
                                            addToContract: newVal,
                                            ...(newVal === true && !prev.isPrimaryContact ? { isSecondaryContact: true } : {}),
                                            ...(newVal === false && !prev.isPrimaryContact ? { isSecondaryContact: false } : {}),
                                        }));
                                    }}
                                >
                                    Add to contract
                                </div>
                                {showHomeOwner && (
                                    <div 
                                        className={`obd-contact-modal__type-tile ${form.homeowner ? 'obd-contact-modal__type-tile--selected' : ''} ${(isHomeOwnerReadOnly || isContactTypeLocked) ? 'obd-contact-modal__type-tile--disabled' : ''}`}
                                        onClick={() => {
                                            if (isContactTypeLocked || isHomeOwnerReadOnly) return;
                                            const newVal = !form.homeowner;
                                            if (newVal) {
                                                const hoCount = getTotalHomeownerCount();
                                                if (hoCount >= 2) {
                                                    setWarningDialog({
                                                        title: 'Homeowner Limit',
                                                        content: 'You can not select more than two homeowners. Please unselect anyone and try again.',
                                                    });
                                                    return;
                                                }
                                            }

                                            setFormWithValidation((prev) => ({ ...prev, homeowner: newVal }));
                                        }}
                                    >
                                        Homeowner
                                    </div>
                                )}
                                <div 
                                    className={`obd-contact-modal__type-tile ${form.onTitle ? 'obd-contact-modal__type-tile--selected' : ''}`}
                                    onClick={() => {
                                        if (isContactTypeLocked) return;
                                        update('onTitle', !form.onTitle);
                                    }}
                                >
                                    On title
                                </div>
                            </div>
                        </section>

                        <section className="obd-contact-modal__section">
                            <div className="obd-contact-modal__mailing">
                                <h3 className="obd-contact-modal__section-title">Mailing address</h3>
                                <Checkbox
                                    checked={form.sameAsInstallationAddress}
                                    onChange={(e) => {
                                        if (isCustomerInfoAndAddressLocked) return;
                                        const checked = e.target.checked;
                                        setFormWithValidation((prev) => {
                                            const next = { ...prev, sameAsInstallationAddress: checked };
                                            if (checked) {
                                                setMailingAddressBackup({
                                                    mailingStreet: prev.mailingStreet,
                                                    mailingCity: prev.mailingCity,
                                                    mailingState: prev.mailingState,
                                                    mailingZip: prev.mailingZip,
                                                });

                                                if (opportunityDetails?.InstallationAddress) {
                                                    const addr = opportunityDetails.InstallationAddress;
                                                    next.mailingStreet = addr.Street || addr.street || '';
                                                    next.mailingCity = addr.City || addr.city || '';
                                                    next.mailingState = addr.State || addr.state || addr.stateCode || '';
                                                    next.mailingZip = addr.ZipCode || addr.zipCode || '';
                                                }
                                            } else if (mailingAddressBackup) {
                                                next.mailingStreet = mailingAddressBackup.mailingStreet || '';
                                                next.mailingCity = mailingAddressBackup.mailingCity || '';
                                                next.mailingState = mailingAddressBackup.mailingState || '';
                                                next.mailingZip = mailingAddressBackup.mailingZip || '';
                                            }
                                            return next;
                                        });
                                    }}
                                    className="obd-contact-modal__checkbox"
                                    disabled={isCustomerInfoAndAddressLocked}
                                >
                                    Same as installation address
                                </Checkbox>
                            </div>

                            {!form.sameAsInstallationAddress && (
                                <div className="obd-contact-modal__fields">
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">Street *</label>
                                        <Input
                                            value={form.mailingStreet}
                                            onChange={(e) => update('mailingStreet', e.target.value)}
                                            placeholder="Street"
                                            className={`obd-contact-modal__input ${fieldError('mailingStreet') ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {fieldError('mailingStreet') ? <div className="invalid-feedbacks">{fieldError('mailingStreet')}</div> : null}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">City *</label>
                                        <Input
                                            value={form.mailingCity}
                                            onChange={(e) => update('mailingCity', e.target.value)}
                                            placeholder="City"
                                            className={`obd-contact-modal__input ${fieldError('mailingCity') ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {fieldError('mailingCity') ? <div className="invalid-feedbacks">{fieldError('mailingCity')}</div> : null}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">State *</label>
                                        <OBDSelect
                                            value={form.mailingState || undefined}
                                            onChange={(v) => update('mailingState', v)}
                                            options={states}
                                            placeholder="State"
                                            className={`obd-contact-modal__select ${fieldError('mailingState') ? 'is-invalid-select' : ''}`}
                                            popupClassName={`obd-select-dropdown obd-contact-select-dropdown obd-theme-${theme}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {fieldError('mailingState') ? <div className="invalid-feedbacks">{fieldError('mailingState')}</div> : null}
                                    </div>
                                    <div className="obd-contact-modal__field">
                                        <label className="obd-contact-modal__label-required">Zip Code *</label>
                                        <Input
                                            value={form.mailingZip}
                                            onChange={(e) => update('mailingZip', e.target.value)}
                                            placeholder="Zip code"
                                            className={`obd-contact-modal__input ${fieldError('mailingZip') ? 'is-invalid' : ''}`}
                                            disabled={isCustomerInfoAndAddressLocked}
                                        />
                                        {fieldError('mailingZip') ? <div className="invalid-feedbacks">{fieldError('mailingZip')}</div> : null}
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
    );

    const formBody = (
        <>
            <div className="obd-contact-modal__inner">{formFields}</div>
        </>
    );

    if (!open) return null;

    if (presentation === 'panel') {
        const footerActions = panelFooterPortal
            ? createPortal(
                  <div className="obd-contact-modal__actions obd-contact-modal__actions--footer">{actionButtons}</div>,
                  panelFooterPortal,
              )
            : null;

        return (
            <div className={`obd-contact-modal obd-contact-modal--panel obd-theme-${theme}`}>
                <div className="obd-contact-modal__panel-scroll obd-scroll-minimal">
                    <div className="obd-contact-modal__inner obd-contact-modal__inner--panel">{formFields}</div>
                </div>
                {footerActions}
                {confirmDialogs}
            </div>
        );
    }

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title={displayName}
            footer={footerActions}
            width={720}
            theme={theme}
            className={`obd-contact-modal obd-theme-${theme}`}
            destroyOnClose
            centered
            maskClosable={false}
        >
            {formBody}
            {confirmDialogs}
        </DashboardModal>
    );
};

export default ContactManageModal;
